
import { createContext, ReactNode, useContext } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiV2 } from '../services/api';
import LocalStorageService from '../services/storage';

const localStorageService = LocalStorageService();

interface Subsidiary {
    id_subsidiary: string;
    subsidiary_name: string;
    cnpj: string;
    isactive?: boolean;
}
type SubsidiaryInput = Omit<Subsidiary, 'id_subsidiary'>

interface SubsidiariesProvidersProps {
    children: ReactNode;
}

interface ResponseProps {
    success: boolean;
    message: string;
}

interface SubsidiariesContextData {
    createSubsidiary: (subsidiary: SubsidiaryInput) => Promise<ResponseProps>;
    updateSubsidiary: (subsidiary: Subsidiary) => Promise<ResponseProps>;
    updateOnDelete: (id: number) => Promise<ResponseProps>;
}

interface UseSubsidiariesOptions {
    loadSubsidiaries?: boolean;
}

interface UseSubsidiariesData extends SubsidiariesContextData {
    subsidiaries: Subsidiary[];
}

const SubsidiariesContext = createContext<SubsidiariesContextData>(
    {} as SubsidiariesContextData
);

const subsidiariesQueryKey = ['subsidiaries'];
const emptySubsidiaries: Subsidiary[] = [];

function hasToken() {
  return Boolean(localStorageService.getIdToken());
}

async function fetchSubsidiaries() {
  const response = await apiV2.get('subsidiaries');
  return Array.isArray(response?.data) ? response.data : [];
}

export function SubsidiariesProvider({ children }: SubsidiariesProvidersProps) {
  const queryClient = useQueryClient();

  function invalidateSubsidiaries() {
    queryClient.invalidateQueries({ queryKey: subsidiariesQueryKey });
  }

  async function updateOnDelete(id: number) {
    const response = await apiV2.delete(`/subsidiaries/${id}`);
    if (response.status === 200) {
      invalidateSubsidiaries();
      return {
        success: true,
        message: "Subsidiária removida com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao remover subsidiária."
    };
  }

  async function createSubsidiary(subsidiariesInput: SubsidiaryInput) {
    const response = await apiV2.post('/subsidiaries', subsidiariesInput);
    if (response.status === 201) {
      invalidateSubsidiaries();
      return {
        success: true,
        message: "Subsidiária adicionada com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao adicionar subsidiária."
    };

  }

  async function updateSubsidiary(subsidiary: Subsidiary) {
    const response = await apiV2.put(`/subsidiaries/${subsidiary.id_subsidiary}`, subsidiary);
    if (response.status === 204) {
      invalidateSubsidiaries();
      return {
        success: true,
        message: "Subsidiária editada com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao editar subsidiária."
    };
  }

  return (
    <SubsidiariesContext.Provider value={{ createSubsidiary, updateSubsidiary, updateOnDelete }}>
      {children}
    </SubsidiariesContext.Provider>
  );
}

export function UseSubsidiaries(options: UseSubsidiariesOptions = {}): UseSubsidiariesData {
  const context = useContext(SubsidiariesContext);
  const { loadSubsidiaries = false } = options;

  const subsidiariesQuery = useQuery({
    queryKey: subsidiariesQueryKey,
    queryFn: fetchSubsidiaries,
    enabled: loadSubsidiaries && hasToken(),
    staleTime: 5 * 60 * 1000
  });

  return {
    ...context,
    subsidiaries: subsidiariesQuery.data || emptySubsidiaries
  };
}
